import React, { useEffect, useState } from 'react';
import {
  Table, Button, Tag, Modal, Input, Form, message, Typography, Space, Badge, List, Spin, Tooltip,
} from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined, FileOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../api/client';

const { Title } = Typography;
const { TextArea } = Input;

interface Doctor {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  iin: string | null;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  has_documents: boolean;
}

interface DocMeta {
  id: string;
  doc_type: string;
  label: string;
  filename: string;
  content_type: string;
  uploaded_at: string;
}

export default function Doctors() {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState<{ open: boolean; id: string | null }>({
    open: false, id: null,
  });
  const [rejectLoading, setRejectLoading] = useState(false);
  const [form] = Form.useForm();

  const [docsModal, setDocsModal] = useState<{ open: boolean; doctor: Doctor | null }>({ open: false, doctor: null });
  const [docs, setDocs] = useState<DocMeta[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/doctors/pending');
      setDoctors(data);
    } catch {
      message.error('Не удалось загрузить список');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function openDocs(doctor: Doctor) {
    setDocsModal({ open: true, doctor });
    setDocsLoading(true);
    try {
      const { data } = await api.get(`/admin/doctors/${doctor.id}/documents`);
      setDocs(data);
    } catch {
      message.error('Не удалось загрузить документы');
    } finally {
      setDocsLoading(false);
    }
  }

  async function viewFile(docId: string) {
    const win = window.open('', '_blank');
    try {
      const { data } = await api.get(`/admin/documents/${docId}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url;
    } catch {
      if (win) win.close();
      message.error('Не удалось открыть файл');
    }
  }

  async function handleVerify(id: string, name: string) {
    try {
      await api.post(`/admin/doctors/${id}/verify`);
      message.success(`Врач ${name} верифицирован`);
      setDoctors((prev) => prev.filter((d) => d.id !== id));
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка');
    }
  }

  async function handleReject(values: { reason: string }) {
    if (!rejectModal.id) return;
    setRejectLoading(true);
    try {
      await api.post(`/admin/doctors/${rejectModal.id}/reject`, { reason: values.reason });
      message.success('Заявка отклонена');
      setDoctors((prev) => prev.filter((d) => d.id !== rejectModal.id));
      setRejectModal({ open: false, id: null });
      form.resetFields();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка');
    } finally {
      setRejectLoading(false);
    }
  }

  const columns: ColumnsType<Doctor> = [
    {
      title: 'Врач',
      dataIndex: 'full_name',
      render: (name, rec) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>ИИН: {rec.iin ?? '—'}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>{rec.phone ?? '—'}</div>
        </div>
      ),
    },
    {
      title: 'Документы',
      render: (_, rec) => (
        <Space direction="vertical" size={4}>
          <Tag
            color={rec.has_documents ? 'green' : 'red'}
            style={{ cursor: 'pointer' }}
            icon={<FileOutlined />}
            onClick={() => openDocs(rec)}
          >
            {rec.has_documents ? 'Загружены' : 'Не загружены'}
          </Tag>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDocs(rec)}>
            Просмотреть
          </Button>
        </Space>
      ),
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'created_at',
      render: (v) => new Date(v).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Действия',
      render: (_, rec) => (
        <Space direction="vertical" size={6}>
          <Tooltip title={!rec.has_documents ? 'Нельзя верифицировать без документов' : ''}>
            <Button
              type="primary" icon={<CheckOutlined />} size="small"
              disabled={!rec.has_documents}
              onClick={() => handleVerify(rec.id, rec.full_name)}
              block
              style={{ maxWidth: 160 }}
            >
              Верифицировать
            </Button>
          </Tooltip>
          <Button
            danger icon={<CloseOutlined />} size="small"
            onClick={() => setRejectModal({ open: true, id: rec.id })}
            block
            style={{ maxWidth: 160 }}
          >
            Отклонить
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <Space wrap>
          <Title level={4} style={{ margin: 0 }}>Врачи на верификации</Title>
          <Badge count={doctors.length} color="#f59e0b" />
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
      </div>

      <Table
        columns={columns}
        dataSource={doctors}
        rowKey="id"
        loading={loading}
        locale={{ emptyText: '✅ Нет врачей, ожидающих верификации' }}
        pagination={{ pageSize: 20 }}
        bordered
        scroll={{ x: 720 }}
      />

      {}
      <Modal
        title={`Документы: ${docsModal.doctor?.full_name ?? ''}`}
        open={docsModal.open}
        onCancel={() => setDocsModal({ open: false, doctor: null })}
        footer={null}
        width={580}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {docsLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        ) : docs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#ef4444' }}>
            <FileOutlined style={{ fontSize: 40, marginBottom: 12 }} />
            <p style={{ fontWeight: 600 }}>Документы не загружены</p>
            <p style={{ color: '#6b7280', fontSize: 13 }}>Врач ещё не прикрепил документы. Верификация невозможна.</p>
          </div>
        ) : (
          <List
            dataSource={docs}
            renderItem={(doc) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => viewFile(doc.id)}
                  >
                    Открыть
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={<FileOutlined style={{ fontSize: 24, color: '#2563eb', marginTop: 4 }} />}
                  title={<span style={{ fontWeight: 600 }}>{doc.label}</span>}
                  description={
                    <div>
                      <div style={{ fontSize: 12 }}>{doc.filename}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                        Загружен: {new Date(doc.uploaded_at).toLocaleString('ru-RU')}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      <Modal
        title="Причина отказа"
        open={rejectModal.open}
        onCancel={() => { setRejectModal({ open: false, id: null }); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleReject}>
          <Form.Item
            name="reason"
            label="Укажите причину отклонения заявки"
            rules={[{ required: true, message: 'Обязательное поле' }]}
          >
            <TextArea rows={4} placeholder="Например: предоставлены недействительные документы" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setRejectModal({ open: false, id: null }); form.resetFields(); }}>
                Отмена
              </Button>
              <Button type="primary" danger htmlType="submit" loading={rejectLoading}>
                Отклонить
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
